import * as Sentry from '@sentry/vue'
import { systemApi } from '@/globals/typescript/utils/api/systemClient'
import { ENV } from './host'
import { useAppStore } from '@/stores/appStore'

/**
 * @description Reports a critical error to Sentry and sends a Slack message to the
 * "lab-log-kritisk" channel, linking the two.
 *
 * The event ID goes to lab-cloud rather than a finished URL: the endpoint is
 * unauthenticated, and building the URL in one server-side place is what keeps the
 * next change to Sentry's URL form from needing three frontend deploys (MIL-267).
 *
 * A call with no `error` produces no Sentry event and its alert reads `-`. There is
 * nothing to capture — inventing an Error here would report our own reporting code.
 * The same applies on localhost, where `main.ts` deliberately never initialises
 * Sentry: `captureException` still hands back an event ID with no client behind it,
 * which would put a link to a non-existent event into the alert.
 *
 * The localhost check is `ENV.isLocalhost` so that this file and `reportError.ts`
 * two doors down answer "is Sentry actually running?" the same way, rather than
 * each inventing its own test for one condition.
 */
export async function handleCriticalError({
  title,
  messages = [],
  debugMessages = [],
  code,
  error,
}: {
  title: string
  messages?: string[]
  debugMessages?: string[]
  code?: string
  error?: Error
}): Promise<void> {
  try {
    const appStore = useAppStore()
    const appVersion = appStore.appVersion

    let sentryEventID: string | undefined
    if (error && !ENV.isLocalhost) {
      Sentry.withScope((scope) => {
        scope.setExtra('title', title)
        scope.setExtra('code', code)
        scope.setExtra('debugMessages', [...messages, ...debugMessages])
        sentryEventID = Sentry.captureException(error)
      })
    }

    const result = await systemApi('post', '/messages/app-error', {
      body: {
        app: `Lab management (${appVersion})`,
        title,
        code,
        messages: [
          `url: ${window?.location?.href}`,
          ...(error ? [error.message] : []),
          ...messages,
          ...debugMessages,
        ],
        appID: 'lab-management',
        sentryEventID,
      },
    })

    // This client answers a failed call with `success: false` rather than throwing,
    // so without this the alert could silently never arrive. Logged rather than
    // thrown: this is the last step of handling an error that already happened, and
    // a throw here would surface as a second failure in place of the real one.
    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error('Failed to report critical error:', title, result)
    }
  } finally {
    // Do nothing
  }
}

